export const MAX_LOD_FILES = 128;
export const MAX_LOD_MANIFEST_BYTES = 5 * 1024 * 1024;
export const MAX_LOD_CHUNK_BYTES = 128 * 1024 * 1024;
export const MAX_LOD_SWITCH_DISTANCE = 1_000_000;
export const MAX_LOD_TRANSITIONS = 15;

/** Scene-unit distances from the camera to a streamed chunk's bound. */
export function parseLodDistances(value: unknown, levels: number): number[] {
	if (
		!Number.isInteger(levels) ||
		levels < 2 ||
		levels > MAX_LOD_TRANSITIONS + 1 ||
		!Array.isArray(value) ||
		value.length !== levels - 1 ||
		value.some(
			(distance, index) =>
				typeof distance !== "number" ||
				!Number.isFinite(distance) ||
				distance <= 0 ||
				distance > MAX_LOD_SWITCH_DISTANCE ||
				(index > 0 && distance <= value[index - 1]),
		)
	)
		throw new Error("Set one increasing positive switch distance for each reduced LOD level.");
	return [...value];
}

/** Original-relative point ratios, stored to reopen the generation settings. */
export function parseLodLevelRatios(value: unknown, levels: number): number[] {
	if (
		!Number.isInteger(levels) ||
		levels < 2 ||
		levels > MAX_LOD_TRANSITIONS + 1 ||
		!Array.isArray(value) ||
		value.length !== levels - 1 ||
		value.some(
			(ratio, index) =>
				typeof ratio !== "number" ||
				!Number.isFinite(ratio) ||
				ratio <= 0 ||
				ratio >= 1 ||
				(index > 0 && ratio >= value[index - 1]),
		)
	)
		throw new Error("Set one decreasing point ratio between zero and one for each reduced LOD level.");
	return [...value];
}
export type LodManifest = {
	version: number;
	lodLevels: number;
	filenames: string[];
	tree: unknown;
	[key: string]: unknown;
};

/** Validate the bounded tree before either previewing or publishing its references. */
export function parseLodManifest(value: unknown): LodManifest {
	function fail(): never {
		throw new Error("Invalid LOD manifest. Choose lod-meta.json and its bundled .sog files.");
	}
	const m = value as LodManifest;
	if (
		!m ||
		m.version !== 1 ||
		!Number.isInteger(m.lodLevels) ||
		m.lodLevels < 2 ||
		m.lodLevels > 16 ||
		!Array.isArray(m.filenames) ||
		!m.filenames.length ||
		m.filenames.length > MAX_LOD_FILES ||
		m.filenames.some(name => typeof name !== "string" || !/^[A-Za-z0-9_-]+\.sog$/.test(name)) ||
		new Set(m.filenames).size !== m.filenames.length ||
		m.environment
	)
		fail();
	const pending = [m.tree];
	let visited = 0;
	let references = 0;
	while (pending.length) {
		const node = pending.pop() as {
			bound?: { min?: number[]; max?: number[] };
			children?: unknown[];
			lods?: Record<string, { file: number; offset: number; count: number }>;
		};
		if (++visited > 20000 || !node || typeof node !== "object") fail();
		const { min, max } = node.bound ?? {};
		if (
			!Array.isArray(min) ||
			!Array.isArray(max) ||
			min.length !== 3 ||
			max.length !== 3 ||
			!min.every((v, i) => Number.isFinite(v) && Number.isFinite(max[i]) && v <= max[i])
		)
			fail();
		if (node.children) {
			if (!Array.isArray(node.children) || node.children.length !== 2) fail();
			pending.push(...node.children);
		} else {
			if (!node.lods || typeof node.lods !== "object" || !Object.keys(node.lods).length) fail();
			for (const [level, ref] of Object.entries(node.lods)) {
				if (
					!/^\d+$/.test(level) ||
					Number(level) >= m.lodLevels ||
					!ref ||
					!Number.isInteger(ref.file) ||
					ref.file < 0 ||
					ref.file >= m.filenames.length ||
					!Number.isSafeInteger(ref.offset) ||
					ref.offset < 0 ||
					!Number.isSafeInteger(ref.count) ||
					ref.count <= 0
				)
					fail();
				references++;
			}
		}
	}
	if (!references) fail();
	return m;
}
